/*
 * The rules of the "new device" form, without React: the shape of the form,
 * where each schema field is stored, validation, how a template is poured
 * into the form, and the request body the API receives.
 */
import { DeviceCreate } from '../../../api/models/DeviceCreate';
import { DeviceTypeEnum } from '../../../api/models/DeviceTypeEnum';
import { HardwareFieldDefinition } from '../../../services/typesService';
import { CustomField } from '../../../types/configTemplate';
import { DEFAULT_GROUPING_NAME } from '../../configTemplates/templateSchema';

export interface DeviceFormData {
  idDevice: string;
  idClient: string;
  deviceType: string;
  model: string;
  syslogHost: string;
  syslogPort: string;
  syslogFacility: string;
  provisioning: string;
  keepaliveTimer: string;
  password: string;
  mqttHost: string;
  mqttPort: string;
  mqttUser: string;
  mqttPass: string;
  dateTime: string;
  phone: string;
  needsRefresh: boolean;
}

export type FormErrors = Record<string, string>;
/** Values of the schema fields that are not columns of the device: sent after creation as custom fields. */
export type CustomValues = Record<string, string>;

export const emptyDeviceForm = (): DeviceFormData => ({
  idDevice: '',
  idClient: '',
  deviceType: '',
  model: '',
  syslogHost: '',
  syslogPort: '',
  syslogFacility: '',
  provisioning: '',
  keepaliveTimer: '',
  password: '',
  mqttHost: '',
  mqttPort: '',
  mqttUser: '',
  mqttPass: '',
  dateTime: new Date().toISOString().slice(0, 16),
  phone: '',
  needsRefresh: false,
});

/**
 * The schema fields that are real columns of a device (snake_case in the
 * schema, camelCase in the form). Any other field is a custom value.
 */
const FIELD_NAME_TO_FORM_KEY: Record<string, keyof DeviceFormData> = {
  mqtt_host: 'mqttHost',
  mqtt_port: 'mqttPort',
  mqtt_user: 'mqttUser',
  mqtt_pass: 'mqttPass',
  syslog_host: 'syslogHost',
  syslog_port: 'syslogPort',
  syslog_facility: 'syslogFacility',
  keepalive_timer: 'keepaliveTimer',
  password: 'password',
  provisioning: 'provisioning',
  phone: 'phone',
};

export const formKeyOf = (fieldName: string): keyof DeviceFormData | undefined => FIELD_NAME_TO_FORM_KEY[fieldName];

/** MAC-style colons and dotted identifiers are allowed alongside letters, digits, dash and underscore. */
export const DEVICE_ID_RE = /^[a-zA-Z0-9_:.-]+$/;

/** Keys of a template's json_config that describe the template itself, not a device field. */
const TEMPLATE_METADATA_KEYS = new Set(['generate_password', 'password', 'custom_fields']);

export interface SchemaGroup {
  name: string;
  fields: HardwareFieldDefinition[];
}

/** The schema fields by group, in schema order (the password row included). */
export const groupFields = (schema: HardwareFieldDefinition[] | null): SchemaGroup[] => {
  if (!schema) return [];
  const byGroup = new Map<string, HardwareFieldDefinition[]>();
  for (const def of schema) {
    const name = def.grouping_name || DEFAULT_GROUPING_NAME;
    if (!byGroup.has(name)) byGroup.set(name, []);
    byGroup.get(name)!.push(def);
  }
  return Array.from(byGroup.entries()).map(([name, fields]) => ({ name, fields }));
};

/** Whether any field declares a default: gates the "Fill defaults" button. */
export const hasDefaults = (schema: HardwareFieldDefinition[] | null): boolean =>
  Boolean(schema?.some((def) => def.default_value != null && def.default_value !== ''));

/** Splits schema-keyed values between the form columns and the custom values. */
export const partitionValues = (values: Record<string, string>): { formPatch: Partial<DeviceFormData>; customPatch: CustomValues } => {
  const formPatch: Record<string, string> = {};
  const customPatch: CustomValues = {};
  for (const [name, value] of Object.entries(values)) {
    const key = formKeyOf(name);
    if (key) formPatch[key] = value;
    else customPatch[name] = value;
  }
  return { formPatch: formPatch as Partial<DeviceFormData>, customPatch };
};

/** The schema defaults, as text, for every field that has one. */
export const schemaDefaultValues = (schema: HardwareFieldDefinition[]): Record<string, string> => {
  const values: Record<string, string> = {};
  for (const def of schema) {
    if (def.default_value != null && def.default_value !== '') values[def.field_name] = String(def.default_value);
  }
  return values;
};

const asText = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean' || typeof value === 'number') return String(value);
  return JSON.stringify(value);
};

const randomPassword = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from(crypto.getRandomValues(new Uint8Array(12))).map((b) => chars[b % chars.length]).join('');
};

export interface TemplateContribution {
  formPatch: Partial<DeviceFormData>;
  customPatch: CustomValues;
  /** Undefined when the template says nothing about the password: the typed value stays. */
  password?: string;
  /** Legacy templates store extra fields as a custom_fields list; kept for the editor below the form. */
  customFields: CustomField[];
}

/**
 * What a template brings to the form. Every top-level key of its json_config
 * goes to the matching column or to the custom values; generate_password
 * wins over an explicit password.
 */
export const templateContribution = (jsonConfig: Record<string, any>): TemplateContribution => {
  const values: Record<string, string> = {};
  for (const [key, raw] of Object.entries(jsonConfig)) {
    if (TEMPLATE_METADATA_KEYS.has(key) || raw === undefined || raw === null) continue;
    values[key] = asText(raw);
  }
  const { formPatch, customPatch } = partitionValues(values);

  let password: string | undefined;
  if (jsonConfig.generate_password === true) password = randomPassword();
  else if (jsonConfig.password) password = String(jsonConfig.password);

  const customFields: CustomField[] = Array.isArray(jsonConfig.custom_fields)
    ? jsonConfig.custom_fields.map((f: CustomField) => ({ ...f, source: 'template' as const, default: f.default ?? f.value }))
    : [];
  for (const f of customFields) {
    if (f.name && f.value !== undefined && f.value !== null) customPatch[f.name] = typeof f.value === 'string' ? f.value : String(f.value);
  }

  return { formPatch, customPatch, password, customFields };
};

type Translate = (key: any, params?: Record<string, string | number>) => string;

/**
 * Identity fields are always required. Schema fields are checked only when
 * the schema marks them required; ports and numbers get a range check.
 */
export const validateDeviceForm = (form: DeviceFormData, schema: HardwareFieldDefinition[] | null, custom: CustomValues, t: Translate): FormErrors => {
  const errors: FormErrors = {};
  if (!form.idDevice.trim()) errors.idDevice = t('errors.fieldRequired');
  else if (!DEVICE_ID_RE.test(form.idDevice)) errors.idDevice = t('errors.invalidDeviceId');
  if (!form.idClient.trim()) errors.idClient = t('errors.fieldRequired');
  if (!form.deviceType.trim()) errors.deviceType = t('errors.fieldRequired');

  for (const def of schema || []) {
    if (!def.is_required) continue;
    const key = formKeyOf(def.field_name);
    const value = String((key ? form[key] : custom[def.field_name]) ?? '').trim();
    if (!value) {
      errors[def.field_name] = t('errors.fieldRequired');
      continue;
    }
    const n = Number(value);
    if (def.value_type === 'port' || def.field_name.endsWith('_port')) {
      if (Number.isNaN(n) || n <= 0 || n > 65535) errors[def.field_name] = t('errors.invalidPort');
    }
    if (def.value_type === 'number') {
      if (Number.isNaN(n)) errors[def.field_name] = t('errors.fieldRequired');
      else if (def.min_value != null && n < def.min_value) errors[def.field_name] = t('errors.minValue', { value: def.min_value });
      else if (def.max_value != null && n > def.max_value) errors[def.field_name] = t('errors.maxValue', { value: def.max_value });
    }
  }
  return errors;
};

/** The typed device handed to the parent once created. */
export const toDeviceCreate = (form: DeviceFormData): DeviceCreate => ({
  idDevice: form.idDevice.trim(),
  idClient: form.idClient.trim(),
  syslogHost: form.syslogHost.trim(),
  syslogPort: Number(form.syslogPort),
  syslogFacility: form.syslogFacility.trim(),
  provisioning: form.provisioning.trim(),
  keepaliveTimer: Number(form.keepaliveTimer),
  password: form.password.trim(),
  mqttHost: form.mqttHost.trim(),
  mqttPort: Number(form.mqttPort),
  mqttUser: form.mqttUser.trim(),
  mqttPass: form.mqttPass.trim(),
  dateTime: new Date(form.dateTime),
  deviceType: form.deviceType.trim() ? (form.deviceType as DeviceTypeEnum) : undefined,
  phone: form.phone.trim() || null,
  needsRefresh: form.needsRefresh || null,
});

const optionalText = (value: string): string | undefined => (value.trim() ? value : undefined);
/** An empty numeric field means "absent", never 0. */
const optionalNumber = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * The POST /v2/conf body. The configuration keys (syslog, MQTT, provisioning,
 * keepalive) are sent only when typed: the backend must never receive a
 * placeholder for a field the gateway type does not define.
 */
export const buildCreatePayload = (form: DeviceFormData, templateId: number | null): Record<string, unknown> => {
  const device = toDeviceCreate(form);
  const payload: Record<string, unknown> = {
    idDevice: device.idDevice,
    idClient: device.idClient,
    syslog_host: optionalText(form.syslogHost),
    syslog_port: optionalNumber(form.syslogPort),
    syslog_facility: optionalText(form.syslogFacility),
    provisioning: optionalText(form.provisioning),
    keepalive_timer: optionalNumber(form.keepaliveTimer),
    password: device.password || '',
    mqtt_host: optionalText(form.mqttHost),
    mqtt_port: optionalNumber(form.mqttPort),
    mqtt_user: optionalText(form.mqttUser),
    mqtt_pass: optionalText(form.mqttPass),
    date_time: device.dateTime.toISOString(),
    deviceType: device.deviceType || null,
    phone: device.phone || null,
    needsRefresh: device.needsRefresh === true,
    model_ref: form.model || null,
    template_id: templateId || null,
  };
  for (const key of Object.keys(payload)) {
    if (payload[key] === undefined) delete payload[key];
  }
  return payload;
};

/** The custom values worth sending: the ones that were typed. */
export const customFieldList = (custom: CustomValues): Array<{ name: string; value: string }> =>
  Object.entries(custom).filter(([, v]) => v !== undefined && v !== null && v !== '').map(([name, value]) => ({ name, value }));

/** A readable message for a failed creation, from the API detail when there is one. */
export const explainCreateError = (error: any, form: DeviceFormData, t: Translate): string => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((err: any) => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
  if (typeof detail === 'string') {
    if (detail.includes('ForeignKeyViolation') && detail.includes('client')) return t('devices.create.clientMissing');
    if (detail.includes('duplicate key') || detail.includes('already exists')) return t('devices.create.alreadyExists', { id: form.idDevice });
    return detail;
  }
  return t('errors.createDeviceFailed');
};
